import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { claimsAPI, membersAPI, beneficiariesAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const STATUS_META = {
  submitted:        { label: 'Submitted',        bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  under_review:     { label: 'Under Review',     bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-500'    },
  pending_approval: { label: 'Board Approval',   bg: 'bg-violet-100',  text: 'text-violet-700',  dot: 'bg-violet-500'  },
  approved:         { label: 'Approved',         bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  rejected:         { label: 'Rejected',         bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-500'     },
  paid:             { label: 'Paid',             bg: 'bg-orange-100',    text: 'text-orange-600',    dot: 'bg-orange-500'    },
};

const TYPE_LABELS = { death: 'Death', disability: 'Disability', other: 'Other' };

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.bg} ${m.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

export default function Claims() {
  const { user } = useAuthStore();
  const [claims, setClaims]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [stats, setStats]           = useState(null);
  const [activeTab, setActiveTab]   = useState('');
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1, has_next: false, has_previous: false });
  const [showModal, setShowModal]   = useState(false);
  const searchTimer = useRef(null);

  // Fetch list
  useEffect(() => {
    setLoading(true);
    claimsAPI.list(page, 20, activeTab, search)
      .then(res => {
        setClaims(res.claims || []);
        setPagination({
          total:       res.total || 0,
          total_pages: res.total_pages || 1,
          has_next:    !!res.has_next,
          has_previous:!!res.has_previous,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, activeTab, search]);

  // Fetch stats once
  useEffect(() => {
    claimsAPI.stats().then(setStats).catch(() => {});
  }, []);

  function handleSearch(e) {
    const v = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(v); setPage(1); }, 300);
  }

  function onClaimCreated() {
    setShowModal(false);
    setPage(1);
    setLoading(true);
    claimsAPI.list(1, 20, activeTab, search)
      .then(res => {
        setClaims(res.claims || []);
        setPagination({ total: res.total||0, total_pages:res.total_pages||1, has_next:!!res.has_next, has_previous:!!res.has_previous });
      })
      .finally(() => setLoading(false));
    claimsAPI.stats().then(setStats).catch(() => {});
  }

  const TABS = [
    { key: '',                 label: 'All',           count: stats?.total            },
    { key: 'submitted',        label: 'Submitted',     count: stats?.submitted        },
    { key: 'under_review',     label: 'Under Review',  count: stats?.under_review     },
    { key: 'pending_approval', label: 'Board Approval',count: stats?.pending_approval },
    { key: 'approved',         label: 'Approved',      count: stats?.approved         },
    { key: 'rejected',         label: 'Rejected',      count: stats?.rejected         },
    { key: 'paid',             label: 'Paid',          count: stats?.paid             },
  ];

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Claims</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {stats ? `${stats.total} claims · TZS ${Number(stats.total_paid_amount || 0).toLocaleString()} paid` : 'Loading…'}
          </p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowModal(true)}
            className="rounded-xl bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 text-sm font-semibold transition-colors"
          >
            + New Claim
          </button>
        )}
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[
            { label: 'Submitted',    value: stats.submitted,    color: 'border-amber-300 bg-amber-50',   tc: 'text-amber-700'   },
            { label: 'Under Review', value: stats.under_review, color: 'border-blue-300 bg-blue-50',     tc: 'text-blue-700'    },
            { label: 'Approved',     value: stats.approved,     color: 'border-emerald-300 bg-emerald-50',tc: 'text-emerald-700' },
            { label: 'Rejected',     value: stats.rejected,     color: 'border-red-300 bg-red-50',       tc: 'text-red-700'     },
            { label: 'Paid',         value: stats.paid,         color: 'border-orange-300 bg-orange-50',     tc: 'text-orange-600'    },
          ].map(s => (
            <button key={s.label}
              onClick={() => { setActiveTab(s.label.toLowerCase().replace(' ', '_')); setPage(1); }}
              className={`rounded-2xl border-2 p-4 text-left transition-all hover:shadow-md ${s.color}`}>
              <p className={`text-2xl font-bold ${s.tc}`}>{s.value ?? 0}</p>
              <p className="text-xs font-medium text-slate-500 mt-0.5">{s.label}</p>
            </button>
          ))}
        </div>
      )}

      {/* Tabs + Search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setPage(1); }}
              className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${
                activeTab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {t.label}
              {t.count != null && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  activeTab === t.key ? 'bg-slate-100 text-slate-600' : 'bg-slate-200 text-slate-500'
                }`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z"/>
          </svg>
          <input type="text" placeholder="Search member or claim no…" defaultValue={search}
            onChange={handleSearch}
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
        ) : claims.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-1">
            <p className="text-slate-500 font-medium text-sm">No claims found</p>
            <p className="text-xs text-slate-400">Try a different filter or search</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Claim No</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Member</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Date</th>
                    <th className="px-4 py-3 w-20" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {claims.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">{c.claim_no}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 text-sm">{c.member_name || '—'}</p>
                        <p className="text-xs text-slate-400 font-mono">{c.member_roll_no || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 capitalize">{TYPE_LABELS[c.claim_type] || c.claim_type}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3 text-xs font-semibold text-slate-700">
                        {c.total_amount ? `TZS ${Number(c.total_amount).toLocaleString()}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{c.date_reported}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/claims/${c.id}`}
                          className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors">
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile list */}
            <ul className="sm:hidden divide-y divide-slate-100">
              {claims.map(c => (
                <li key={c.id}>
                  <Link to={`/claims/${c.id}`}
                    className="flex items-center gap-3 px-4 py-3.5 active:bg-slate-50 transition-colors">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white text-xs font-bold ${
                      c.claim_type === 'death' ? 'bg-slate-600' : c.claim_type === 'disability' ? 'bg-violet-600' : 'bg-orange-500'
                    }`}>
                      {(TYPE_LABELS[c.claim_type] || 'O')[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900 truncate flex-1">{c.member_name || '—'}</p>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400 font-mono">{c.claim_no}</span>
                        <span className="text-slate-200">·</span>
                        <span className="text-xs text-slate-400">{c.date_reported}</span>
                      </div>
                    </div>
                    <svg className="h-4 w-4 text-slate-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6"/>
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
              <p className="text-xs text-slate-400">
                Page {page} of {pagination.total_pages} · {pagination.total.toLocaleString()} total
              </p>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!pagination.has_previous}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  ← Prev
                </button>
                <button onClick={() => setPage(p => p + 1)} disabled={!pagination.has_next}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* New Claim Modal */}
      {showModal && (
        <NewClaimModal onClose={() => setShowModal(false)} onCreated={onClaimCreated} />
      )}
    </div>
  );
}

// ── New Claim Modal ───────────────────────────────────────────────────────────

function NewClaimModal({ onClose, onCreated }) {
  const navigate = useNavigate();
  const [memberSearch, setMemberSearch]     = useState('');
  const [memberResults, setMemberResults]   = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberKin, setMemberKin]           = useState([]);
  const [claimType, setClaimType]           = useState('death');
  const [dateOfIncident, setDateOfIncident] = useState('');
  const [notes, setNotes]                   = useState('');
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState('');
  const searchTimer = useRef(null);

  function searchMembers(q) {
    if (!q.trim()) { setMemberResults([]); return; }
    membersAPI.list(1, 8, q).then(res => setMemberResults(res.members || [])).catch(() => {});
  }

  function handleMemberInput(e) {
    const v = e.target.value;
    setMemberSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchMembers(v), 250);
  }

  function selectMember(m) {
    setSelectedMember(m);
    setMemberSearch(m.full_name);
    setMemberResults([]);
    // Load their kin list for preview
    beneficiariesAPI.getByMember(m.id)
      .then(res => setMemberKin(Array.isArray(res) ? res : (res.beneficiaries || [])))
      .catch(() => setMemberKin([]));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedMember) { setError('Please select a member'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await claimsAPI.create({
        member_id:        selectedMember.id,
        claim_type:       claimType,
        date_of_incident: dateOfIncident || null,
        notes:            notes || null,
      });
      navigate(`/claims/${res.claim_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create claim');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800">New Claim</h2>
          <button onClick={onClose} className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {error && (
            <p className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{error}</p>
          )}

          {/* Member search */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Member *</label>
            <div className="relative">
              <input type="text" value={memberSearch} onChange={handleMemberInput}
                placeholder="Search by name or roll number…"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {memberResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                  {memberResults.map(m => (
                    <button key={m.id} type="button" onClick={() => selectMember(m)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left">
                      <div className="h-8 w-8 shrink-0 rounded-full bg-orange-600 flex items-center justify-center text-white text-xs font-bold">
                        {(m.full_name || '?')[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{m.full_name}</p>
                        <p className="text-xs text-slate-400 font-mono">{m.roll_no}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedMember && (
              <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center gap-3">
                <div className="h-8 w-8 shrink-0 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xs font-bold">
                  {selectedMember.full_name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-emerald-800">{selectedMember.full_name}</p>
                  <p className="text-xs text-emerald-600 font-mono">{selectedMember.roll_no} · {selectedMember.chapter || '—'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Claim type */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Claim Type *</label>
            <div className="grid grid-cols-3 gap-2">
              {[['death','Death'],['disability','Disability'],['other','Other']].map(([val, lab]) => (
                <button key={val} type="button" onClick={() => setClaimType(val)}
                  className={`rounded-xl border-2 py-2.5 text-sm font-medium transition-all ${
                    claimType === val
                      ? 'border-orange-500 bg-orange-50 text-orange-600'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}>
                  {lab}
                </button>
              ))}
            </div>
          </div>

          {/* Date of incident */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Date of Incident</label>
            <input type="date" value={dateOfIncident} onChange={e => setDateOfIncident(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Initial Notes</label>
            <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any initial notes about this claim…"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
          </div>

          {/* Beneficiaries preview */}
          {selectedMember && memberKin.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Next of Kin ({memberKin.length})</p>
              <div className="space-y-1.5">
                {memberKin.map(k => (
                  <div key={k.id} className="flex items-center gap-2">
                    <div className="h-6 w-6 shrink-0 rounded-full bg-slate-400 flex items-center justify-center text-white text-[10px] font-bold">
                      {(k.full_name || '?')[0]}
                    </div>
                    <span className="text-xs text-slate-700 font-medium">{k.full_name}</span>
                    <span className="text-xs text-slate-400 capitalize">· {(k.relationship || '').toLowerCase()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting || !selectedMember}
              className="flex-1 rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white py-2.5 text-sm font-semibold transition-colors">
              {submitting ? 'Submitting…' : 'Submit Claim'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
